import { Box, Button, Divider, Flex, Group, LoadingOverlay, Modal, Select, SimpleGrid, Space, Text, Textarea, TextInput } from '@mantine/core';
import React, { memo, useContext, useEffect, useState } from 'react';
import { authContext } from '../../../../../context/AuthProvider.component';
import { notifications } from '@mantine/notifications';
import { baseUrl } from '../../../../../Constants/constant';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';

interface DisposalProps {
    onComplete: () => void;
    onBack: () => void;
}
interface DisposalPayload {
    compute_id: string;
    end_life_id: string | null;
    is_draft: number;
    step: string;
    file_name: string | null;
}
interface SelectOption {
    value: string;
    label: string;
}

const Disposal = ({ onBack, onComplete }: DisposalProps) => {
    const [opened, { open, close }] = useDisclosure(false);
    const navigate = useNavigate();
    const [value, setValue] = useState<string | null>(null);
    // const [reportComment, setReportComment] = useState<string>('');
    const [editId, setEditId] = useState<number | null>();
    const [error, setErrors] = useState<Record<string, string>>({});
    const [fileName, setFileName] = useState<string>('');
    const [payloads, setPayloads] = useState<DisposalPayload | null>(null);
    const [saveMode, setSaveMode] = useState<'next' | 'draft' | 'skip'>('next');
    const [disposalType, setDisposalType] = useState<SelectOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { computeId, draftLca, setDraftLca } = useContext<any>(authContext);
    const token = localStorage.getItem("token") || "";

    // State for API data
    const [getDisposalData, setGetDisposalData] = useState<any>(null);
    const [DisposalData, setDisposalData] = useState<any>(null);
    const [disposalLoading, setDisposalLoading] = useState(false);

    // Fetch disposal data function
    const fetchDisposalData = async (url: string) => {
        try {
            setDisposalLoading(true);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "token": token,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            setGetDisposalData(result);
        } catch (error) {
            console.error('Error fetching disposal data:', error);
            showNotification('error', 'Error loading disposal data');
        } finally {
            setDisposalLoading(false);
        }
    };

    // Fetch disposal types function
    const getDisposal = async (url: string) => {
        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "token": token,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            setDisposalData(result);
        } catch (error) {
            console.error('Error fetching disposal types:', error);
            showNotification('error', 'Error loading disposal types');
        }
    };
    useEffect(() => {
        if (computeId) {
            fetchDisposalData(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
        }
    }, [computeId]);

    useEffect(() => {
        getDisposal(`${baseUrl}admin/end-life`);
    }, []);

    useEffect(() => {
        if (DisposalData?.data) {
            const TransportOptions = DisposalData?.data?.rows?.map((el: any) => ({
                value: el?.id?.toString(),
                label: el?.name,
            }));
            setDisposalType(TransportOptions);
        }
    }, [DisposalData]);

    useEffect(() => {
        if (getDisposalData?.data?.rows?.length > 0) {
            const computeJourneys = getDisposalData?.data?.rows[0]?.ComputeDisposal;
            setEditId(computeJourneys?.id)
            if (computeJourneys?.end_life_id) {
                setValue(computeJourneys?.end_life_id?.toString());
            }
            // if (computeJourneys?.report_comment) {
            //     setReportComment(computeJourneys?.report_comment);
            // }
        }
    }, [getDisposalData]);


    const showNotification = (type: 'success' | 'error', message: string) => {
        notifications.show({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            color: type === 'success' ? 'green' : 'red',
        });
    };

    const handleChange = (field: string, value: string | null) => {
        if (field === "end_of_life") {
            setValue(value);
        }
        if (field === "file_name") {
            setFileName(value || '');
        }
        // if (field === "report_comment") {
        //     setReportComment(value || '');
        // }

        setErrors((prev: any) => ({ ...prev, [field]: undefined }));
    };

    const validateFields = () => {
        let newErrors: Record<string, string> = {};

        if (!value) {
            newErrors.end_of_life = "End Life is Required";
        }
        if (saveMode === 'draft' && (!fileName || fileName.trim() === '')) {
            newErrors.file_name = "File name is Required";
        } else if (fileName && (fileName.length < 3 || fileName.length > 50)) {
            newErrors.file_name = "File name must be between 3 and 50 characters";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return false;
        }
        return true;
    };

    const handleSave = async (e: any, mode: 'next' | 'draft' | 'skip') => {
        e.preventDefault();
        if (mode === 'skip') {
            onComplete()
            return;
        }
        setSaveMode(mode);
        mode === 'next' ? setDraftLca(0) : setDraftLca(1);
        if (!validateFields()) {
            showNotification('error', 'Please fill all required fields');
            return;
        }
        const newPayload = {
            compute_id: computeId,
            end_life_id: value,
            // report_comment: reportComment,
            is_draft: mode === 'draft' ? 1 : 0,
            step: "Disposal",
            ...(editId && { id: editId }),
            file_name: fileName || null,
        };
        setPayloads(newPayload);

    };

    useEffect(() => {
        const makeApiCall = async () => {
            if (payloads) {
                const url = editId
                    ? `${baseUrl}compute/lca/updateDisposal`
                    : `${baseUrl}compute/lca/addDisposal`;

                try {
                    setIsLoading(true);
                    
                    const response = await fetch(url, {
                        method: editId ? 'PUT' : 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Access-Control-Allow-Origin": "*",
                            token: token,
                        },
                        body: JSON.stringify(payloads)
                    });
                    
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    
                    const result = await response.json();
                    
                    // Handle the response immediately after getting it
                    if (saveMode === 'draft') {
                        // Close modal first
                        close();
                        
                        // Show success notification
                        showNotification('success', 'File Saved in Draft Successfully');
                        
                        // Navigate to draft page
                        navigate('/draft');
                    } else {
                        // For next mode, call onComplete immediately
                        onComplete();
                        showNotification('success', result?.message || 'Disposal Data Saved Successfully');
                    }
                    
                    // Reset payloads to prevent duplicate processing
                    setPayloads(null);
                    
                } catch (error) {
                    console.error('API Error:', error);
                    showNotification('error', 'An error occurred while saving');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        makeApiCall();
    }, [payloads, editId, token, saveMode, close, navigate, onComplete]);

    return (
        <div>
            <LoadingOverlay
                visible={disposalLoading}
                zIndex={1000}
                overlayProps={{
                    radius: 'sm',
                    blur: 2,
                    style: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }
                }}
                loaderProps={{ color: '#404040', type: 'bars' }}
            />
            <Box>
                <Text size="xl" pb={5} fw={500}>Disposal</Text>
                <SimpleGrid cols={3}>
                    <Select
                        data={disposalType}
                        value={value}
                        onChange={(val) => handleChange("end_of_life", val)}
                        label="End of Life"
                        placeholder="Select End of Life"
                        error={error?.["end_of_life"]}
                    />
                </SimpleGrid>
                <Space h={'md'} />
                {/* <Textarea
                    label="Comments"
                    placeholder="Enter your Comments"
                    withAsterisk
                    variant="filled"
                    autosize
                    value={reportComment}
                    onChange={(e) => handleChange("report_comment", e.target.value)}
                    minRows={2}
                    maxRows={4}
                    radius={6}
                    error={error?.["report_comment"]}
                /> */}
                <Space h={'xl'} />
            </Box>
            <Modal centered opened={opened} onClose={close} title="Save in Draft">
                <TextInput
                    autoFocus
                    label="File Name"
                    value={fileName}
                    onChange={(e) => handleChange("file_name", e.target.value)}
                    variant="filled"
                    withAsterisk
                    mb="lg"
                    placeholder="Please enter file name"
                    radius={6}
                    error={error?.file_name}
                />
                <Divider h={'md'} />
                <Flex justify="flex-end">
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'draft')}
                        size="md"
                    >
                        Save
                    </Button>
                </Flex>

            </Modal>
            <Flex justify="flex-end">
                <Group>
                    <Button variant="default" radius={6} size="md" onClick={open}>
                        Save to Draft
                    </Button>
                    <Button variant="default" radius={6} size="md" onClick={onBack}>
                        Back
                    </Button>
                    <Button
                        variant="default"
                        radius={6}
                        onClick={(e) => handleSave(e, 'skip')}
                        size="md"
                    >
                        Skip
                    </Button>
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'next')}
                        loading={isLoading}
                        disabled={isLoading}
                        size="md"
                    >
                        Next
                    </Button>
                </Group>
            </Flex>
        </div>
    );
};

export default memo(Disposal);